import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REDIRECT_SECONDS = 5;

const copy = {
  fr: {
    heading: "Paiement confirmé !",
    subheading: "Merci pour votre inscription à la Conférence Internationale Ottawa 2027.",
    line1: "Votre paiement a été traité avec succès.",
    line2: "Votre accès au programme complet, aux conférencières et aux ressources a été débloqué.",
    line3: "Votre lettre d'invitation visa sera disponible dans votre espace personnel dans les 5 jours ouvrables.",
    redirectingIn: "Redirection vers votre espace personnel dans",
    seconds: "secondes",
    goNow: "Accéder maintenant à mon espace",
    verifying: "Vérification de votre paiement…",
  },
  en: {
    heading: "Payment Confirmed!",
    subheading: "Thank you for registering for the Ottawa International Conference 2027.",
    line1: "Your payment has been processed successfully.",
    line2: "Access to the full program, speakers, and resources has been unlocked.",
    line3: "Your visa invitation letter will be available in your dashboard within 5 business days.",
    redirectingIn: "Redirecting to your dashboard in",
    seconds: "seconds",
    goNow: "Go to my dashboard now",
    verifying: "Verifying your payment…",
  },
};

export default function PaymentConfirmation() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const t = copy[language] || copy.fr;
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [verifying, setVerifying] = useState(true);

  // Verify payment server-side (idempotent). Supports both Square and Stripe.
  // Stripe adds ?session_id=cs_... to the URL. Square has no query.
  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      if (!token) { setVerifying(false); return; }

      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');

      try {
        if (sessionId) {
          // Stripe: poll status up to 5 times at 2s intervals
          const headers = { Authorization: `Bearer ${token}` };
          let attempts = 0;
          while (attempts < 5 && !cancelled) {
            const res = await axios.get(
              `${BACKEND_URL}/api/payment/stripe/status/${encodeURIComponent(sessionId)}`,
              { headers }
            );
            if (res.data?.payment_status === 'paid') break;
            await new Promise((r) => setTimeout(r, 2000));
            attempts += 1;
          }
        } else {
          // Square: server-side verification via existing endpoint
          await axios.post(
            `${BACKEND_URL}/api/payment/verify`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } catch (err) {
        // Non-blocking — user will see status when they land in dashboard
      }
      if (!cancelled) setVerifying(false);
    };

    verify();
    return () => { cancelled = true; };
  }, [token]);

  // Auto-redirect countdown
  useEffect(() => {
    if (verifying) return;
    if (countdown <= 0) {
      navigate('/dashboard', { replace: true });
      return;
    }
    const timer = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, verifying, navigate]);

  return (
    <div
      data-testid="payment-confirmation-page"
      className="min-h-screen bg-gradient-to-br from-[#FDF2F8] via-white to-[#FFF6FA] flex items-center justify-center px-4"
    >
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-[#CD0000]/10 p-8 md:p-10 text-center">
        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 ring-8 ring-green-50/40">
          <CheckCircle2 className="w-12 h-12 text-green-500" strokeWidth={2.2} />
        </div>

        <h1 className="text-3xl md:text-4xl font-extrabold text-[#1D1D1D] mb-3">
          {t.heading}
        </h1>
        <p className="text-gray-600 mb-6 leading-relaxed">{t.subheading}</p>

        <div className="text-left space-y-3 bg-gray-50 rounded-2xl p-5 mb-8">
          <p className="text-sm text-gray-700 flex items-start gap-2">
            <span className="text-green-500 font-bold shrink-0">✓</span>
            <span>{t.line1}</span>
          </p>
          <p className="text-sm text-gray-700 flex items-start gap-2">
            <span className="text-green-500 font-bold shrink-0">✓</span>
            <span>{t.line2}</span>
          </p>
          <p className="text-sm text-gray-700 flex items-start gap-2">
            <span className="text-green-500 font-bold shrink-0">✓</span>
            <span>{t.line3}</span>
          </p>
        </div>

        {verifying ? (
          <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t.verifying}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              {t.redirectingIn} <span className="font-bold text-[#CD0000]">{countdown}</span> {t.seconds}
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard', { replace: true })}
              data-testid="payment-confirmation-go-dashboard"
              className="inline-flex items-center justify-center gap-2 bg-[#CD0000] hover:bg-[#A00000] text-white font-bold py-3 px-8 rounded-xl transition-colors"
            >
              {t.goNow}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
